import { OneEuroFilter, type OneEuroOptions } from "./one-euro-filter";
import { quatNormalize, type Quat } from "./math";

const dot = (a: Quat, b: Quat): number => a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
const negate = (q: Quat): Quat => ({ x: -q.x, y: -q.y, z: -q.z, w: -q.w });

/**
 * Stabilizes a gyro orientation stream by 1€-filtering each quaternion component and
 * renormalizing. Used on the 3DoF/gyro path only — never on the WebXR anchored path, which
 * ARCore already drift-corrects.
 */
export class QuaternionStabilizer {
  private readonly filters: [OneEuroFilter, OneEuroFilter, OneEuroFilter, OneEuroFilter];
  private previous: Quat | null = null;

  constructor(options?: OneEuroOptions) {
    this.filters = [
      new OneEuroFilter(options),
      new OneEuroFilter(options),
      new OneEuroFilter(options),
      new OneEuroFilter(options),
    ];
  }

  filter(q: Quat, dt: number): Quat {
    // Double cover: q and −q are the same rotation. Flip to the previous sample's hemisphere so
    // the per-component filters never blend across the antipodal jump (which would collapse the
    // magnitude toward zero).
    const aligned = this.previous && dot(this.previous, q) < 0 ? negate(q) : q;

    const smoothed = quatNormalize({
      x: this.filters[0].filter(aligned.x, dt),
      y: this.filters[1].filter(aligned.y, dt),
      z: this.filters[2].filter(aligned.z, dt),
      w: this.filters[3].filter(aligned.w, dt),
    });

    this.previous = smoothed;
    return smoothed;
  }

  reset(): void {
    for (const filter of this.filters) filter.reset();
    this.previous = null;
  }
}
